import { useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import CuppingSection from './components/CuppingSection';
import Benefits from './components/Benefits';
import ProphetQuotes from './components/ProphetQuotes';
import Pricing from './components/Pricing';
import Testimonials from './components/Testimonials';
import Appointment from './components/Appointment';
import MapSection from './components/MapSection';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import { BRAND, ADDRESS_LINES } from '@/constants';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://lifecupp.com';

const schemaData = [
  {
    '@context': 'https://schema.org',
    '@type': ['HealthAndBeautyBusiness', 'LocalBusiness'],
    '@id': `${SITE_URL}/#business`,
    name: BRAND.name,
    description: `Cabinet de cupping thérapie et bien-être à ${BRAND.city}. Ventouses pour douleurs, stress et récupération. Consultation initiale gratuite.`,
    url: SITE_URL,
    telephone: BRAND.phoneE164,
    email: BRAND.email,
    image: BRAND.logoUrl,
    logo: BRAND.logoUrl,
    address: {
      '@type': 'PostalAddress',
      streetAddress: ADDRESS_LINES.street,
      addressLocality: BRAND.city,
      postalCode: BRAND.postalCode,
      addressCountry: BRAND.country,
      addressRegion: BRAND.region,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 46.1938,
      longitude: 6.2358,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '19:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '09:00',
        closes: '17:00',
      },
    ],
    priceRange: '€€',
    currenciesAccepted: 'EUR',
    paymentAccepted: 'Cash, Card',
    sameAs: [
      'https://www.instagram.com/lifecupp.74',
      'https://www.tiktok.com/@lifecupp.74',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Soins Cupping & bien-être',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Séance avec 3 zones',
            description:
              'Séance complète incluant le tirage de 3 zones au choix avec ventouses en bambou et en verre.',
          },
          price: '60',
          priceCurrency: 'EUR',
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Protocole Complet 5 zones',
            description:
              'Séance sur 5 zones, serviette enflammée et massage oculaire.',
          },
          price: '120',
          priceCurrency: 'EUR',
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Ventouse Récupération Musculaire',
            description: 'Protocole sportif sur 1 zone au choix.',
          },
          price: '40',
          priceCurrency: 'EUR',
        },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}/#webpage`,
    url: SITE_URL,
    name: `${BRAND.name} ${BRAND.city} — Cupping Thérapie ${BRAND.postalCode}`,
    description: `Cabinet ${BRAND.name} à ${BRAND.city} (${BRAND.postalCode}). Cupping thérapie, ventouses et bien-être. Consultation initiale gratuite.`,
    inLanguage: 'fr-FR',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#business` },
    dateModified: '2026-08-04',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: BRAND.name,
    inLanguage: 'fr-FR',
    publisher: { '@id': `${SITE_URL}/#business` },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Qu’est-ce que le cupping thérapie ?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Le cupping (thérapie par ventouses) crée une succion sur la peau pour stimuler la circulation et soulager les tensions musculaires.',
        },
      },
      {
        '@type': 'Question',
        name: 'La consultation initiale est-elle gratuite ?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Oui, la consultation initiale chez ${BRAND.name} à ${BRAND.city} est gratuite. Elle permet d’évaluer votre situation et de définir le protocole adapté.`,
        },
      },
      {
        '@type': 'Question',
        name: `Où se situe le cabinet ${BRAND.name} ?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Le cabinet est situé au ${ADDRESS_LINES.full}, ${BRAND.region}.`,
        },
      },
      {
        '@type': 'Question',
        name: 'Quels sont les tarifs ?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Séance 3 zones 60€, Protocole Complet 5 zones 120€, Récupération sportive 40€. Forfait plusieurs séances −15% pour les athlètes. Consultation initiale gratuite.',
        },
      },
    ],
  },
];

export default function HomePage() {
  useEffect(() => {
    const existingScripts = document.querySelectorAll('script[data-schema="lifecupp"]');
    existingScripts.forEach((s) => s.remove());

    schemaData.forEach((schema, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'lifecupp');
      script.setAttribute('data-schema-index', String(i));
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema="lifecupp"]').forEach((s) => s.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#f6f3ec]">
      <Navbar />
      <main>
        <Hero />
        <About />
        <CuppingSection />
        <Benefits />
        <ProphetQuotes />
        <Pricing />
        <Testimonials />
        <Appointment />
        <MapSection />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
